import { useDebouncedValue } from '@mantine/hooks';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CampaignIcon from '@mui/icons-material/Campaign';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MuiDashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import HelpIcon from '@mui/icons-material/Help';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MapIcon from '@mui/icons-material/Map';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import SensorsIcon from '@mui/icons-material/Sensors';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import {
  ButtonBase,
  ButtonBaseProps,
  Collapse,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { Box, lighten, SystemStyleObject } from '@mui/system';
import { BloxNXTIcon } from 'assets/BloxNXTIcon';
import { BloxAudienceIcon } from 'assets/icons/BloxAudience';
import { BloxContentIcon } from 'assets/icons/BloxContent';
import { BloxDesignIcon } from 'assets/icons/BloxDesign';
import ContactsIcon from 'assets/icons/Contacts';
import DashboardIcon from 'assets/icons/Dashboard';
import FeedsIcon from 'assets/icons/Feeds';
import FileFolderIcon from 'assets/icons/FileFolder';
import SettingsIcon from 'assets/icons/Settings';
import { BetaFlag } from 'components/generic/BetaFlag';
import React, {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  checkHasProDataInsightsPermission,
  checkEditorialAccessPermission,
  checkEditorialSettingsPermission,
  checkHasAnyBroadcastPermission,
  checkHasAnySyndicationPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import {
  baseTransition,
  getBorderString,
  getBoxShadows,
  getNavTextColor,
  getPrimaryBgColor,
  navBaseFontSize,
  navBaseIconSize,
  navBorderRadiusPx,
  navExtraSmallFontSize,
  navLargeFontSize,
  navSmallerIconSize,
  navSpaceFactor,
  navTinyFontSize,
} from 'utility/styles';
import { GetUserMeData } from 'utility/types/dataProvider';
import { BloxNXTLogo } from '../../../assets/BloxNXTLogo';
import { TSessionConfigData, useCosmos } from '../../../providers/contexts/CosmosContext';
import { bloxColors, bloxIconColors } from '../../../themes/constants';
import { getCommunityAccessDetails, getHelpUrl } from '../functions';
import { useEditorialAiSearchState } from 'components/Search/components/SearchAdvance/components/AISearch/useEditorialAiSearchState';
import {
  getEditorialSearchShortcutPaths,
  getActiveOpenNavItems,
  setupNavItems,
} from './leftNavFunctions';
import { hasBetaProgram } from 'utility/experiments';
import { useCosmosLayout } from 'providers/contexts/CosmosLayoutContext';

// --------------------------
// Types
// --------------------------
export type NavItem = {
  reactKey: string; // Any stable unique key for react + used in id/testid.
  label: string; // Text label for the item.
  path?: string; // Where this links to. Don't set on categories.
  matchPaths?: string[]; // What paths to match for this item to set as active/open
  children?: ChildNavItem[]; // If children exist, item becomes a category.
  icon?: React.ComponentType<SvgIconProps> | React.FunctionComponent<SvgIconProps>; // MUI icon component, or converted with MUI createSvgIcon.
  iconProps?: SvgIconProps; // Props spread to MUI icon component.
  disableLink?: true; // Explicitly disable if needed.
  beta?: boolean; // If true, show the beta flag.
  hidden?: boolean; // If true, hide the item in the nav.
  divider?: boolean; // If true, render a divider instead of a nav item.
};
// Child navigation item type - essentially a NavItem without children, and cannot be a header.
export type ChildNavItem = Omit<NavItem, 'children'>;

export type KeyBoolMap = Record<NavItem['reactKey'], boolean>; // For tracking active/open items by reactKey in the nav.

export type NavSetup = {
  hasHeader: boolean; // If true the header will be hidden and items will be shown within navcontent area.
};
// Note: The "Header" (e.g. "Content Management") is the first item in the array after the NavSetup object.
// If hasHeader is true. Else, we skip the header and all items are directly rendered in the NavContent area. (categories)

export type NavConfig = [NavSetup, ...NavItem[]]; // Tuple whre first item is config, rest is nav items.

// Size style for custom blox icons.
const bloxIconInputProps = {
  sx: {
    fontSize: 20, // navBaseIconSize - 4px
  },
};

// --------------------------
// Get Nav Configurations
// --------------------------

export type NavConfigFnParams = {
  app?: string;
  sessionConfig?: TSessionConfigData;
  userInfo?: GetUserMeData;
  isAiSearchActive?: boolean;
};

export function getRootNavCfg({ sessionConfig }: NavConfigFnParams): NavConfig {
  const { granted: hasProDataInsights } =
    checkHasProDataInsightsPermission(sessionConfig);
  const hasBetaAccess = hasBetaProgram(sessionConfig);
  const hasCustomDashboards = hasProDataInsights && hasBetaAccess;

  const items: NavConfig = [
    {
      hasHeader: true,
    },
    {
      reactKey: 'rootHeaderNav',
      label: 'blox.navigation.root.home',
      icon: BloxNXTIcon,
      iconProps: {
        sx: () => ({
          color: bloxColors.logoblue,
        }),
      },
    },
    {
      reactKey: 'rootHomeNav',
      label: 'blox.navigation.dashboards.custom_dashboards.title_alt',
      path: '/',
      matchPaths: ['/'],
      icon: DashboardIcon,
      iconProps: bloxIconInputProps,
      // For BC. Show beta flag and preserve rootHomeNav's link when hasCustomDashboards=false.
      beta: hasCustomDashboards,
      ...(hasCustomDashboards && {
        children: [
          {
            reactKey: 'customDashboardsSearch',
            label: 'blox.navigation.dashboards.custom_dashboards.title',
            path: '/dashboards',
            matchPaths: ['/dashboards', '/dashboards/*'],
            icon: SearchIcon,
            iconProps: bloxIconInputProps,
            hidden: !hasCustomDashboards,
          },
          {
            reactKey: 'dashboardsDivider',
            divider: true,
            label: 'blox.navigation.common.divider',
            hidden: !hasCustomDashboards,
          },
          {
            reactKey: 'customDashboardsHomeDashboard',
            label: 'blox.navigation.dashboards.home_dashboard',
            path: '/',
            matchPaths: ['/'],
            icon: DashboardIcon,
            iconProps: {
              sx: {
                fontSize: 10,
              },
            },
            hidden: !hasCustomDashboards,
          },
        ],
      }),
    },
    {
      reactKey: 'rootSettings',
      label: 'blox.navigation.settings.label',
      path: '/settings/',
      matchPaths: ['/settings/*'],
      icon: SettingsIcon,
      iconProps: bloxIconInputProps,
    },
  ];
  return items;
}

export function getEditorialNavCfg({
  app,
  sessionConfig,
  isAiSearchActive,
}: NavConfigFnParams): NavConfig {
  // Permissions checks
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasSyndication } = checkHasAnySyndicationPermission(sessionConfig);
  const { granted: userHasEditorial } = checkEditorialAccessPermission(sessionConfig);
  const { granted: userHasEditorialSettings } =
    checkEditorialSettingsPermission(sessionConfig);
  const { granted: userHasNotifier } = checkHasAnyBroadcastPermission(sessionConfig);

  const hasNotifier = Boolean(userIsAdmin || userHasNotifier);
  const hasWireSyndi = Boolean(userIsAdmin || userHasSyndication);
  const hasEditorial = Boolean(userIsAdmin || userHasEditorial);
  const hasEditorialSettings = Boolean(userIsAdmin || userHasEditorialSettings);

  // Editorial search paths and shortcuts
  const {
    editorialSearchPath,
    editorialSearchPathMatch,
    editorialSearchArticlePath,
    editorialSearchVideoPath,
    editorialSearchImagePath,
    editorialSearchCollectionPath,
    editorialTemplatesManagePath,
  } = getEditorialSearchShortcutPaths({
    isAiSearchActive: isAiSearchActive,
  });

  const items: NavConfig = [
    {
      hasHeader: true,
    },
    {
      // Content Management
      label: 'blox.navigation.assets.title',
      reactKey: 'editorialHeaderNav',
      icon: BloxContentIcon,
      iconProps: {
        sx: (theme) => ({
          color:
            theme.palette.mode === 'light'
              ? bloxIconColors.leftnavheader
              : lighten(
                  bloxIconColors.leftnavheader,
                  theme.blox.values.lightenCoefficient,
                ),
        }),
      },
    },
    {
      reactKey: 'editorialCategory',
      label: 'blox.navigation.assets.label',
      icon: FileFolderIcon,
      iconProps: bloxIconInputProps,
      matchPaths: [
        '/editorial/search/*',
        '/editorial/dashboard',
        '/editorial/search/filters',
      ],
      hidden: !hasEditorial,
      children: [
        {
          reactKey: 'editorialAssets',
          label: 'blox.navigation.assets.all',
          path: editorialSearchPath,
          matchPaths: [editorialSearchPathMatch],
          icon: SearchIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialArticles',
          label: 'blox.navigation.assets.articles',
          path: editorialSearchArticlePath,
          matchPaths: [editorialSearchArticlePath],
          icon: DescriptionIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialVideos',
          label: 'blox.navigation.assets.videos',
          path: editorialSearchVideoPath,
          matchPaths: [editorialSearchVideoPath],
          icon: VideocamOutlinedIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialImages',
          label: 'blox.navigation.assets.images',
          path: editorialSearchImagePath,
          matchPaths: [editorialSearchImagePath],
          icon: ImageIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialCollections',
          label: 'blox.navigation.assets.collections',
          path: editorialSearchCollectionPath,
          matchPaths: [editorialSearchCollectionPath],
          icon: CollectionsOutlinedIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialDivider1',
          divider: true,
          label: 'blox.navigation.common.divider',
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialDashboard',
          label: 'blox.navigation.assets.dashboard',
          path: '/editorial/dashboard',
          matchPaths: ['/editorial/dashboard'],
          icon: MuiDashboardIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialVideoDashboard',
          label: 'blox.navigation.assets.video_dashboard',
          path: '/editorial/dashboard/video',
          matchPaths: ['/editorial/dashboard/video'],
          beta: true,
          icon: MuiDashboardIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialQuickFilters',
          label: 'blox.navigation.assets.quick_filters',
          path: '/editorial/search/filters',
          matchPaths: ['/editorial/search/filters'],
          icon: BookmarkIcon,
          hidden: !hasEditorial,
        },
        {
          reactKey: 'editorialTemplates',
          label: 'blox.navigation.assets.templates',
          path: editorialTemplatesManagePath,
          matchPaths: [editorialTemplatesManagePath],
          icon: ContentCopyIcon,
          hidden: !hasEditorial,
        },
      ],
    },
    {
      reactKey: 'syndicationCategory',
      label: 'blox.navigation.feeds.label',
      icon: FeedsIcon,
      iconProps: bloxIconInputProps,
      matchPaths: ['/feeds/*'],
      beta: true,
      hidden: !hasWireSyndi,
      children: [
        {
          reactKey: 'syndicationSearch',
          label: 'blox.navigation.feeds.all',
          path: '/feeds/search',
          matchPaths: ['/feeds/search/'],
          hidden: !hasWireSyndi,
          icon: SearchIcon,
        },
        {
          reactKey: 'syndicationDivider1',
          divider: true,
          label: 'blox.navigation.common.divider',
          hidden: !hasWireSyndi,
        },
        {
          reactKey: 'syndicationQuickFilters',
          label: 'blox.navigation.assets.quick_filters',
          path: '/feeds/search/filters',
          matchPaths: ['/feeds/search/filters'],
          icon: BookmarkIcon,
          hidden: !hasWireSyndi,
        },
        {
          reactKey: 'syndicationChannelsSearch',
          label: 'blox.navigation.feeds.channels',
          path: '/feeds/channels',
          matchPaths: ['/feeds/channels'],
          hidden: !hasWireSyndi,
          icon: SensorsIcon,
        },
      ],
    },
    {
      reactKey: 'notifierCategory',
      label: 'blox.navigation.notifier.title',
      icon: CampaignIcon,
      iconProps: bloxIconInputProps,
      matchPaths: ['/notifier/*'],
      hidden: !hasNotifier,
      beta: true,
      children: [
        {
          reactKey: 'notifierSearch',
          label: 'blox.navigation.notifier.messages',
          path: '/notifier/search',
          matchPaths: ['/notifier/search/*'],
          icon: SearchIcon,
          hidden: !hasNotifier,
        },
        {
          reactKey: 'notifierChannels',
          label: 'blox.navigation.notifier.channels',
          path: '/notifier/channels',
          matchPaths: ['/notifier/channels/*'],
          icon: SensorsIcon,
          hidden: !hasNotifier,
        },
        {
          reactKey: 'notifierConnections',
          label: 'blox.navigation.notifier.connections',
          path: '/notifier/connections',
          matchPaths: ['/notifier/connections/*'],
          icon: ShareOutlinedIcon,
          hidden: !hasNotifier,
        },
      ],
    },
    {
      reactKey: 'editorialSettingsCategory',
      label: 'blox.navigation.settings.label',
      icon: SettingsIcon,
      iconProps: bloxIconInputProps,
      matchPaths: ['/editorial/settings/*'],
      hidden: !(hasEditorialSettings || hasNotifier),
      children: [
        {
          reactKey: 'editorialSettingsAi',
          label: 'blox.editorial.settings.ai.label',
          path: '/editorial/settings/#aiSettings',
          matchPaths: ['/editorial/settings/#aiSettings'],
          hidden: !hasEditorialSettings,
        },
        {
          reactKey: 'editorialSettingsRcs',
          label: 'blox.editorial.settings.relatedContentStyles.label',
          path: '/editorial/settings/#relatedContentStyles',
          matchPaths: ['/editorial/settings/#relatedContentStyles'],
          hidden: !hasEditorialSettings,
        },
        {
          reactKey: 'notifierSettingsAutomatic',
          label: 'blox.navigation.notifier.automatic_notifications',
          path: '/editorial/settings/#automatic-notifications',
          matchPaths: ['/editorial/settings/#automatic-notifications'],
          hidden: !hasNotifier,
        },
      ],
    },
  ];

  return items;
}

/**
 * Community / User
 */
export function getUserAppNavCfg({ sessionConfig }: NavConfigFnParams): NavConfig {
  const {
    hasContacts,
    hasJourneys,
    hasEmailReach,
    hasServices,
    hasReports,
    hasUserMultiSearch,
    hasTransactions,
  } = getCommunityAccessDetails({ sessionConfig: sessionConfig });
  const { granted: hasProDataInsights } =
    checkHasProDataInsightsPermission(sessionConfig);

  const items: NavConfig = [
    {
      hasHeader: true,
    },
    {
      reactKey: 'communityHeaderNav',
      label: 'blox.navigation.user.title', // Community
      icon: BloxAudienceIcon,
      iconProps: {
        sx: (theme) => ({
          color:
            theme.palette.mode === 'light'
              ? bloxIconColors.leftnavheader
              : lighten(
                  bloxIconColors.leftnavheader,
                  theme.blox.values.lightenCoefficient,
                ),
        }),
      },
    },
    {
      reactKey: 'userCategory',
      label: 'blox.navigation.user.contacts',
      icon: ContactsIcon,
      iconProps: bloxIconInputProps,
      matchPaths: ['/user/*'],
      hidden: !hasContacts,
      children: [
        {
          reactKey: 'userSearch',
          label: 'blox.navigation.user.all_contacts',
          path: '/user/account/search',
          matchPaths: ['/user/account/search/*'],
          icon: SearchIcon,
          hidden: !hasContacts,
        },
        {
          reactKey: 'userMultisiteSearch',
          label: 'blox.navigation.user.multisite_search',
          path: '/user/account/multisite/search/',
          matchPaths: ['/user/account/multisite/search/*'],
          icon: ManageSearchIcon,
          hidden: !hasUserMultiSearch,
        },
        {
          reactKey: 'contactsDivider',
          label: 'blox.navigation.common.divider',
          divider: true,
        },
        {
          reactKey: 'userDashboard',
          label: 'blox.navigation.assets.dashboard',
          path: '/user/dashboard',
          matchPaths: ['/user/dashboard'],
          icon: MuiDashboardIcon,
          hidden: !hasContacts,
        },
      ],
    },
    {
      reactKey: 'servicesCategory',
      label: 'blox.navigation.user.services',
      beta: true,
      icon: ReceiptLong,
      matchPaths: ['/services/*'],
      hidden: !hasServices,
      children: [
        {
          reactKey: 'servicesSearch',
          label: 'blox.navigation.user.all_services',
          path: '/services/search',
          matchPaths: ['/services/search/*'],
          icon: SearchIcon,
          hidden: !hasServices,
        },
      ],
    },
    {
      reactKey: 'transactionsCategory',
      label: 'blox.navigation.user.transactions',
      beta: true,
      icon: CreditScoreIcon,
      matchPaths: ['/transactions/*'],
      hidden: !hasTransactions,
      children: [
        {
          reactKey: 'transactionsSearch',
          label: 'blox.navigation.user.transactions_search',
          path: '/transactions/search',
          matchPaths: ['/transactions/search/*', '/transactions/view/*'],
          icon: SearchIcon,
          hidden: !hasTransactions,
        },
      ],
    },
    {
      reactKey: 'reportsCategory',
      label: 'blox.navigation.user.reports',
      beta: true,
      icon: StackedLineChartIcon,
      matchPaths: ['/reports', '/reports/*'],
      hidden: !hasReports,
      children: [
        {
          reactKey: 'reportsSubscription',
          label: 'blox.navigation.user.reports_dashboard',
          path: '/reports/subscription',
          matchPaths: ['/reports', '/reports/*'],
          icon: DescriptionIcon,
          hidden: !hasReports,
        },
      ],
    },
    {
      reactKey: 'journeysCategory',
      label: 'blox.navigation.user.journeys',
      beta: true,
      icon: MeetingRoomOutlinedIcon,
      matchPaths: ['/journeys/*'],
      hidden: !hasJourneys,
      children: [
        {
          reactKey: 'journeysSearch',
          label: 'blox.navigation.user.all_journeys',
          path: '/journeys/search',
          matchPaths: ['/journeys/search/*'],
          icon: SearchIcon,
          hidden: !hasJourneys,
        },
      ],
    },
    {
      reactKey: 'emailreachCategory',
      label: 'blox.navigation.user.email_reach',
      beta: true,
      icon: EmailIcon,
      iconProps: bloxIconInputProps,
      matchPaths: ['/emailreach/*'],
      hidden: !hasEmailReach,
      children: [
        {
          reactKey: 'emailReachContactsSearch',
          label: 'blox.navigation.user.email_reach_contacts',
          path: '/emailreach/contacts/search',
          matchPaths: ['/emailreach/contacts/search/*'],
          icon: PersonSearchIcon,
        },
        {
          reactKey: 'listssearch',
          label: 'blox.navigation.user.email_reach_lists',
          path: '/emailreach/lists/search',
          matchPaths: ['/emailreach/lists/search/*'],
          icon: ManageSearchIcon,
        },
        {
          reactKey: 'emailreachMessagesSearch',
          label: 'blox.navigation.user.email_reach_messages',
          path: '/emailreach/messages/search',
          matchPaths: ['/emailreach/messages/search/*'],
          icon: ManageSearchIcon,
        },
        {
          reactKey: 'emailreachAccountSearch',
          label: 'blox.navigation.user.email_reach_search',
          path: '/emailreach/account/search',
          matchPaths: ['/emailreach/account/search/*'],
          icon: SearchIcon,
          hidden: !hasEmailReach,
        },
        {
          reactKey: 'campaignssearch',
          label: 'Campaigns',
          path: '/emailreach/campaigns/search',
          matchPaths: ['/emailreach/campaigns/search/*'],
          icon: CampaignIcon,
        },
        {
          reactKey: 'emailreachDivider1',
          label: 'blox.navigation.common.divider',
          divider: true,
          hidden: !hasProDataInsights,
        },
        {
          reactKey: 'emailreachDashboard',
          label: 'blox.navigation.assets.dashboard',
          path: '/emailreach/dashboard',
          matchPaths: ['/emailreach/dashboard/*'],
          hidden: !hasProDataInsights,
          icon: DashboardIcon,
          iconProps: {
            sx: {
              fontSize: 10,
            },
          },
        },
      ],
    },
  ];
  return items;
}

export function getDesignAppNavCfg({ app, sessionConfig }: NavConfigFnParams): NavConfig {
  const items: NavConfig = [
    {
      hasHeader: true,
    },
    {
      reactKey: 'designHeaderNav',
      label: 'blox.navigation.design.title',
      icon: BloxDesignIcon,
      iconProps: {
        sx: (theme) => ({
          color:
            theme.palette.mode === 'light'
              ? bloxIconColors.leftnavheader
              : lighten(
                  bloxIconColors.leftnavheader,
                  theme.blox.values.lightenCoefficient,
                ),
        }),
      },
      beta: true,
    },
    {
      reactKey: 'designPageBuilder',
      label: 'blox.navigation.design.maps',
      path: '/design/page-builder/',
      matchPaths: ['/design/page-builder/*'],
      icon: MapIcon,
    },
  ];
  return items;
}

type NavItemRestProps = Omit<
  ButtonBaseProps,
  | 'ref'
  | 'id'
  | 'sx'
  | 'children'
  | 'onClick'
  | 'component'
  | 'disableRipple'
  | 'aria-label'
>;

interface BaseNavItemProps {
  navItem: NavItem;
  handleOnClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  id?: string; // also is data-testid
  sx?: SxProps<Theme>;
  className?: string;
  href?: string | undefined | null;
  hidden?: boolean; // If true, hide the item.
  hideLabel?: boolean; // Hide the text, only show the icon.
  iconOpen?: boolean; // Only for category items, to show the open arrow.
  active?: boolean; // If the item is active to apply the active styles.
  polymorphAs?: any; // Pass to the MUI "Component" prop to polymorph the buttonbase as another element.
  disableRipple?: boolean; // Disable the ripple effect on the buttonbase.
  isDisabled?: boolean; // Disable the buttonbase, can apply to closed state.
  restProps?: NavItemRestProps; // Collect remaining props to pass to the buttonbase, if needed.
}

export const BaseNavItem = memo(
  forwardRef<HTMLButtonElement, BaseNavItemProps>(
    (
      {
        navItem,
        sx,
        handleOnClick,
        hideLabel,
        iconOpen,
        className,
        href,
        hidden,
        active,
        id,
        polymorphAs,
        disableRipple,
        isDisabled,
        restProps,
      },
      ref,
    ) => {
      const { t } = useTranslation();
      const theme = useTheme();
      const { leftNavOpen } = useCosmosLayout();
      const Icon = navItem?.icon; // Extract so we can use JSX syntax rather than direct access for the transpiler/react to treat it as a component.
      const iconProps = navItem?.iconProps;
      const isHidden = hidden ?? false;
      const isDivider = !!navItem?.divider;

      const activeBgColor = getPrimaryBgColor(theme);
      const hoverBgColor = getPrimaryBgColor(theme, true);
      const navTextColor = getNavTextColor(theme);

      if (isDivider) {
        return (
          <Divider
            key={navItem?.reactKey}
            aria-label={t(navItem?.label)}
          />
        );
      }

      return (
        <ButtonBase
          ref={ref}
          id={id}
          data-testid={id}
          onClick={handleOnClick}
          href={isDisabled ? null : href}
          component={polymorphAs}
          className={`${className ? className : ''}${active ? ' itemactive' : ''}${
            isDisabled ? ' itemdisabled' : ''
          }`.trim()}
          disableRipple={disableRipple}
          disabled={Boolean(isDisabled)}
          tabIndex={isDisabled ? -1 : undefined}
          aria-disabled={Boolean(isDisabled)}
          hidden={isHidden}
          aria-hidden={isHidden}
          aria-label={`Category - ${t(navItem?.label) || navItem?.path || 'unknown'}`}
          sx={{
            pointerEvents: isDisabled ? 'none' : undefined,
            display: isHidden ? 'none' : 'flex',
            flexDirection: leftNavOpen ? 'row' : 'column',
            gap: navSpaceFactor,
            borderRadius: navBorderRadiusPx,
            fontSize: leftNavOpen ? navBaseFontSize : navTinyFontSize,
            color: isDisabled ? theme.palette.action.disabled : navTextColor,
            bgcolor: leftNavOpen
              ? active
                ? activeBgColor
                : 'transparent'
              : 'transparent',
            py: navSpaceFactor * 2,
            px: navSpaceFactor,

            '& .betaflag-wrapper': {
              pointerEvents: 'auto', // Allow for beta flag hover state
            },

            '&:hover, &:focus-within': {
              color: !isDisabled ? theme.palette.text.primary : navTextColor,
              bgcolor: leftNavOpen
                ? active
                  ? hoverBgColor
                  : theme.palette.action.hover
                : 'transparent',

              '& .iconwrapper': !leftNavOpen
                ? {
                    bgcolor: active ? hoverBgColor : theme.palette.action.hover,
                  }
                : {},
            },

            '& .iconwrapper': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: navBorderRadiusPx,
              height: leftNavOpen ? 'auto' : '40px',
              width: leftNavOpen ? 'auto' : '40px',
              bgcolor: !leftNavOpen && active ? activeBgColor : 'transparent',
            },

            '& .itemicon': {
              display: 'flex',
              fontSize: navBaseIconSize,
            },

            '& .contentwrapper': {
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              textAlign: leftNavOpen ? 'left' : 'center',
            },

            '& .itemlabel': {
              fontSize: leftNavOpen ? navBaseFontSize : navExtraSmallFontSize,
              flexGrow: leftNavOpen ? 0 : 1, // Centers the label
              alignSelf: 'flex-end',
              overflowWrap: 'anywhere',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },

            '& .expandicon': {
              transform: iconOpen ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: `transform ${baseTransition}`,
              fontSize: navSmallerIconSize,
            },

            ...(sx ? (typeof sx === 'function' ? sx(theme) : sx) : {}),
          }}
          {...restProps}
        >
          {Icon && (
            <Box
              id="iconwrapper-id"
              data-testid="iconwrapper-id"
              className="iconwrapper"
            >
              <Icon
                {...iconProps}
                className="itemicon"
              />
            </Box>
          )}

          <Box
            id="contentwrapper-id"
            data-testid="contentwrapper-id"
            className="contentwrapper"
          >
            {!hideLabel ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  className="itemlabel"
                  id="itemlabel-id"
                  data-testid="itemlabel-id"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  {t(navItem.label)}
                </Typography>
                {navItem.beta && leftNavOpen && (
                  <BetaFlag disabled={Boolean(isDisabled)} />
                )}
              </Box>
            ) : null}

            {navItem?.children?.length && leftNavOpen ? (
              <ExpandLessRoundedIcon
                id="expandicon-id"
                data-testid="expandicon-id"
                className="expandicon"
              />
            ) : null}
          </Box>
        </ButtonBase>
      );
    },
  ),
);
BaseNavItem.displayName = 'BaseNavItem';

interface NavHeaderProps {
  navItem: NavItem | null;
  sx?: SxProps<Theme>;
}

export const NavHeader = memo(({ navItem }: NavHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { leftNavOpen } = useCosmosLayout();

  // Handler placeholder, we haven't decided what the header item does yet, if anything.
  // Note: Header without a path will appear disabled/regular and not clickable.
  const handleHeaderIconClick = useCallback((event: React.MouseEvent) => {
    event?.preventDefault();
  }, []);

  return (
    <Box
      id="area-navheader"
      data-testid="area-navheader"
      aria-label={t('blox.navigation.common.ariaHeader', {
        label: t(navItem?.label || ''),
      })}
      sx={{
        gridArea: 'navheader',
        display: 'flex',
        flexDirection: 'column',
        alignItems: leftNavOpen ? 'flex-start' : 'center',
        px: navSpaceFactor, // Align with the content icons
        my: navSpaceFactor * 2.5,
        width: '100%',
      }}
    >
      {navItem ? (
        <BaseNavItem
          id={`navheader-item-${navItem.reactKey}`}
          navItem={navItem}
          disableRipple={!navItem?.path}
          hidden={navItem?.hidden}
          isDisabled={navItem?.disableLink || true} // doesn't apply to header unless this become a route/actionable button - disabling for now
          handleOnClick={(event) => handleHeaderIconClick(event)}
          hideLabel={!leftNavOpen}
          sx={{
            width: '100%',
            '& .itemlabel': {
              fontSize: navLargeFontSize,
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.primary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .iconwrapper': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: navBorderRadiusPx,
              width: 'auto',
            },
          }}
        />
      ) : null}
    </Box>
  );
});
NavHeader.displayName = 'NavHeader';

interface NavCategoryProps {
  navItem: NavItem;
  openCategories: KeyBoolMap;
  activeKeyMap: KeyBoolMap;
  setOpenCategories: React.Dispatch<React.SetStateAction<KeyBoolMap>>;
}

export const NavCategory = memo(
  ({ navItem, openCategories, setOpenCategories, activeKeyMap }: NavCategoryProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { activeSite } = useCosmos();
    const { t } = useTranslation();
    const { leftNavOpen, setLeftNavOpen } = useCosmosLayout();
    const [isHovered, setIsHovered] = useState(false);
    // nav tooltip timeout to not be annoying if accidentally shortly hovered over
    const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleCategoryToggle = useCallback(
      (event: React.MouseEvent) => {
        event?.preventDefault();
        if (!leftNavOpen) {
          setOpenCategories((prev) => ({
            ...prev,
            [navItem.reactKey]: true, // Always open the category when clicking from a closed bar state.
          }));
          setLeftNavOpen(true); // If the nav was closed, open it.
        } else {
          setOpenCategories((prev) => ({
            ...prev,
            [navItem.reactKey]: !prev[navItem.reactKey], // If the left nav is open just toggle the category
          }));
        }
      },
      [leftNavOpen, navItem.reactKey, setOpenCategories, setLeftNavOpen],
    );

    const handleChildNavItemClick = useCallback(
      async (event: React.MouseEvent, child: ChildNavItem) => {
        event?.preventDefault();
        if (child?.path) {
          switch (child?.path) {
            case '/journeys/search':
            case '/editorial/search':
            case '/editorial/search/templates':
            case '/transactions/search':
            case '/notifier/search':
            case '/feeds/search': {
              // Signal component should reload the default saved search or default sort.
              navigate(`/${activeSite}${child.path}`, {
                state: { reloadDefault: true },
              });
              break;
            }
            default: {
              navigate(`/${activeSite}${child.path}`);
            }
          }
        }
      },
      [activeSite, navigate],
    );

    // Tooltip content for the category.
    const tooltipMenuItems = navItem.children?.map((child) => {
      const isActive = activeKeyMap[child.reactKey];
      const isDisabled = child?.disableLink || !child?.path;

      const activeBgColor = getPrimaryBgColor(theme);
      const hoverBgColor = getPrimaryBgColor(theme, true);
      const navTextColor = getNavTextColor(theme);

      return (
        <BaseNavItem
          id={`navcategory-tooltip-child-${child.reactKey}`}
          key={child.reactKey}
          navItem={child}
          active={isActive}
          isDisabled={isDisabled}
          hidden={child?.hidden}
          disableRipple={!child?.path}
          handleOnClick={(event) => handleChildNavItemClick(event, child)}
          href={child?.path ? `/${activeSite}${child?.path}` : null}
          sx={{
            flexDirection: 'row',
            py: navSpaceFactor * 2,
            px: navSpaceFactor,
            color: isDisabled ? theme.palette.action.disabled : navTextColor,
            '& .itemlabel': {
              fontSize: navBaseFontSize,
              flexGrow: 0,
            },
            '& .itemicon': {
              bgcolor: 'transparent',
              fontSize: 'inherit',
            },
            '& .iconwrapper': {
              height: 'auto',
              width: 'auto',
              bgcolor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
            },
            // Hovers to match baseNavItem without open/closed considerations
            '&:hover, &:focus-within': {
              color: !isDisabled ? theme.palette.text.primary : navTextColor,
              bgcolor: theme.palette.action.hover,
              '& .iconwrapper': {
                bgcolor: isActive ? hoverBgColor : 'transparent',
              },
            },
            // Active state overrides
            ...(isActive && {
              bgcolor: activeBgColor,
              '&:hover, &:focus-within': {
                color: !isDisabled ? theme.palette.text.primary : navTextColor,
                bgcolor: hoverBgColor,
              },
            }),
          }}
        />
      );
    });

    const handleMouseEnter = useCallback(() => {
      if (hoverTimeout?.current) {
        clearTimeout(hoverTimeout.current); // Clear any pending close
      }
      hoverTimeout.current = setTimeout(() => setIsHovered(true), 300);
    }, [hoverTimeout]);

    const handleMouseLeave = useCallback(() => {
      if (hoverTimeout?.current) {
        clearTimeout(hoverTimeout.current);
      }
      // small delay to prevent immediate closing when moving between
      hoverTimeout.current = setTimeout(() => setIsHovered(false), 300);
    }, [hoverTimeout]);

    const isDisabled =
      navItem?.disableLink || (!navItem.children?.length && !navItem?.path);

    return (
      <Tooltip
        title={tooltipMenuItems}
        // Open on hover when nav is closed, but do NOT open for disabled categories.
        open={!leftNavOpen && isHovered && !isDisabled}
        // Dont open this on keyboard nav.
        disableFocusListener={true}
        // Disable these when nav is open.
        disableInteractive={leftNavOpen}
        disableHoverListener={leftNavOpen}
        disableTouchListener={leftNavOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        arrow={false}
        enterNextDelay={300}
        placement="right-start"
        id="navcategory-tooltip-base"
        data-testid="navcategory-tooltip-base"
        className="navcategory-tooltip-base"
        slotProps={{
          tooltip: {
            sx: {
              // tooltip sx prop bug: https://github.com/mui/material-ui/issues/38485
              flexDirection: 'column',
              zIndex: 1500,
              gap: navSpaceFactor,
              p: navSpaceFactor,
              m: 0,
              width: 'fit-content',
              minWidth: 200,
              color: theme.palette.text.primary,
              bgcolor: theme.palette.background.paper,
              outline: getBorderString(theme),
              boxShadow: getBoxShadows(theme),
              '& > *:not(:last-of-type)': {
                mb: navSpaceFactor, // Space between items in tooltip menu
              },
            },
          },

          popper: {
            //Note: there are still some MUI classes that come before style/sx here, check the dom.
            id: 'navcategory-tooltip-popper',
            className: 'navcategory-tooltip-popper',
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            popperOptions: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -9],
                  },
                },
              ],
            },
          },
        }}
      >
        <Box
          id="navcategory-wrapper"
          data-testid="navcategory-wrapper"
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BaseNavItem
            id={`navcategory-header-${navItem.reactKey}`}
            navItem={navItem}
            handleOnClick={(event) => handleCategoryToggle(event)}
            isDisabled={
              navItem?.disableLink || (!navItem.children?.length && !navItem?.path)
            } // Explicit disable if set, or if no children and no path.
            hidden={navItem?.hidden}
            iconOpen={openCategories[navItem.reactKey]}
            // Requested - show active state on closed nav - only active child when open.
            active={!leftNavOpen && activeKeyMap[navItem.reactKey]}
            aria-label={
              leftNavOpen
                ? t('blox.navigation.common.ariaOpen', { label: t(navItem.label) })
                : t('blox.navigation.common.ariaOpen', { label: t(navItem.label) })
            }
            href={null} // Prevent the category from being clickable.
            sx={{
              paddingY: `6px`,
            }}
          />
          <Collapse
            id="navcategory-collapse"
            data-testid="navcategory-collapse"
            in={leftNavOpen && openCategories[navItem.reactKey]}
            timeout="auto"
            sx={{
              display: leftNavOpen ? 'flex' : 'none',
              '& .MuiCollapse-wrapperInner': {
                display: 'flex',
                flexDirection: 'column',
                gap: navSpaceFactor,
                '& > *:first-of-type': {
                  mt: navSpaceFactor,
                },
              },
            }}
          >
            {navItem.children?.map((child) => {
              // future: if parent category is hidden, also hide the children
              // if not hidden, then respect per child hidden state
              // const isHidden = navItem?.hidden || child?.hidden;
              // but for now will define individual hidden states at
              // nav config level to not create any obscure implicit behavior

              return (
                <BaseNavItem
                  id={`navcategory-child-${child.reactKey}`}
                  key={child.reactKey}
                  navItem={child}
                  active={activeKeyMap[child.reactKey]}
                  isDisabled={child?.disableLink || !child?.path}
                  hidden={child?.hidden}
                  disableRipple={!child?.path}
                  href={child?.path ? `/${activeSite}${child?.path}` : null}
                  handleOnClick={(event) => handleChildNavItemClick(event, child)}
                  sx={{
                    pl: leftNavOpen ? navSpaceFactor * 8 : 0, // Indent children of category
                    py: leftNavOpen ? navSpaceFactor * 1.75 : 0,
                    '& .itemicon': {
                      fontSize: `calc(100% + 4px)`, // Slightly larger icon
                    },
                  }}
                />
              );
            })}
          </Collapse>
        </Box>
      </Tooltip>
    );
  },
);
NavCategory.displayName = 'NavCategory';

interface NavContentProps {
  navItems: NavItem[];
  openCategories: KeyBoolMap;
  setOpenCategories: React.Dispatch<React.SetStateAction<KeyBoolMap>>;
  activeKeyMap: KeyBoolMap;
}

export const NavContent = memo(
  ({ navItems, openCategories, setOpenCategories, activeKeyMap }: NavContentProps) => {
    const { activeSite } = useCosmos();
    const navigate = useNavigate();

    const handleBasicNavItemClick = useCallback(
      (event: React.MouseEvent, item: NavItem) => {
        event?.preventDefault();
        navigate(`/${activeSite}${item.path}`);
      },
      [activeSite, navigate],
    );

    return (
      <Box
        id="area-navcontent"
        data-testid="area-navcontent"
        sx={{
          gridArea: 'navcontent',
          overflow: 'auto',
          flexDirection: 'column',
          display: 'flex',
          p: navSpaceFactor,
          gap: navSpaceFactor,
        }}
      >
        {navItems.map((item) => {
          if (item?.children?.length) {
            // Category with children, preferred.
            return (
              <NavCategory
                key={item.reactKey}
                navItem={item}
                openCategories={openCategories}
                setOpenCategories={setOpenCategories}
                activeKeyMap={activeKeyMap}
              />
            );
          } else {
            // One off item w/o children, unlikely but here if needed.
            return (
              <BaseNavItem
                id={`navitem-${item.reactKey}`}
                key={item.reactKey}
                navItem={item}
                disableRipple={!item?.path}
                isDisabled={item?.disableLink || !item?.path}
                hidden={item?.hidden}
                href={item?.path ? `/${activeSite}${item?.path}` : null}
                active={activeKeyMap[item.reactKey]}
                handleOnClick={(event) => handleBasicNavItemClick(event, item)}
              />
            );
          }
        })}
      </Box>
    );
  },
);
NavContent.displayName = 'NavContent';

type HoveredTooltipsState = {
  helpBtn: boolean;
  expandBtn: boolean;
};

export const NavFooter = memo(() => {
  const theme = useTheme();
  const { leftNavOpen, setLeftNavOpen } = useCosmosLayout();
  const { activeSite } = useCosmos();
  const navigate = useNavigate();
  const location = useLocation();
  // debouncedLeftNavOpen - Slightly over the transition time.
  // Fixes tooltips stuck in wrong area on close.
  const [debouncedLeftNavOpen] = useDebouncedValue(leftNavOpen, 305);
  const [hoveredTooltips, setHoveredTooltips] = useState<HoveredTooltipsState>({
    helpBtn: false,
    expandBtn: false,
  });

  const buttonBaseSx: SystemStyleObject = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: leftNavOpen ? 'flex-start' : 'center',
    p: navSpaceFactor * 2,
    fontSize: navBaseIconSize,
    color: getNavTextColor(theme),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&:hover, &:focus-within': {
      bgcolor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  };

  const handleHelpButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      // Get the current pathname excluding the activeSite prefix
      const pathname = location.pathname.replace(new RegExp(`^/${activeSite}`), '');
      // Get the appropriate help URL
      const helpUrl = getHelpUrl(pathname);
      // Open the help URL in a new tab
      window.open(helpUrl, '_blank');
    },
    [location.pathname, activeSite],
  );

  return (
    <Box
      component="footer"
      id="area-navfooter"
      data-testid="area-navfooter"
      sx={{
        gridArea: 'navfooter',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.action.hover,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Tooltip
        title="Dashboard"
        placement="right"
        id="navfooter-dashboard-tooltip"
        className="navfooter-dashboard-tooltip"
        disableInteractive
      >
        <ButtonBase
          id="navfooter-dashboard-button"
          data-testid="navfooter-dashboard-button"
          href={`/${activeSite}`}
          sx={buttonBaseSx}
          onClick={(event) => {
            event.preventDefault();
            navigate(`/${activeSite}`);
          }}
        >
          {leftNavOpen ? (
            <BloxNXTLogo
              sx={{
                height: navBaseIconSize * 2,
                width: 'auto',
                maxHeight: navBaseIconSize,
              }}
            />
          ) : (
            <BloxNXTIcon sx={{ fontSize: navBaseIconSize }} />
          )}
        </ButtonBase>
      </Tooltip>
      <Tooltip
        title="Help"
        // When leftNavOpen, no tooltip - else use the deounced value + hover state to show tooltip.
        open={leftNavOpen ? false : !debouncedLeftNavOpen && hoveredTooltips.helpBtn}
        placement="right"
        id="navfooter-help-tooltip"
        className="navfooter-help-tooltip"
        data-testid="navfooter-help-tooltip"
        disableInteractive
      >
        <ButtonBase
          sx={buttonBaseSx}
          onClick={handleHelpButtonClick}
          onMouseEnter={() => setHoveredTooltips((prev) => ({ ...prev, helpBtn: true }))}
          onMouseLeave={() => setHoveredTooltips((prev) => ({ ...prev, helpBtn: false }))}
          onFocus={() => setHoveredTooltips((prev) => ({ ...prev, helpBtn: true }))}
          onBlur={() => setHoveredTooltips((prev) => ({ ...prev, helpBtn: false }))}
          id="navfooter-help-button"
          data-testid="navfooter-help-button"
          aria-label="Help"
        >
          <HelpIcon
            sx={{
              fontSize: navSmallerIconSize,
              width: '24px',
              textAlign: 'center',
            }}
          />
          {leftNavOpen && (
            <Typography
              sx={{
                fontSize: navTinyFontSize,
              }}
            >
              Help
            </Typography>
          )}
        </ButtonBase>
      </Tooltip>

      <Tooltip
        title="Expand"
        open={leftNavOpen ? false : !debouncedLeftNavOpen && hoveredTooltips.expandBtn}
        placement="right"
        id="navfooter-expand-tooltip"
        className="navfooter-expand-tooltip"
        disableInteractive
      >
        <ButtonBase
          sx={buttonBaseSx}
          onClick={() => setLeftNavOpen((prev) => !prev)}
          onMouseEnter={() =>
            setHoveredTooltips((prev) => ({ ...prev, expandBtn: true }))
          }
          onMouseLeave={() =>
            setHoveredTooltips((prev) => ({ ...prev, expandBtn: false }))
          }
          onFocus={() => setHoveredTooltips((prev) => ({ ...prev, expandBtn: true }))}
          onBlur={() => setHoveredTooltips((prev) => ({ ...prev, expandBtn: false }))}
          data-testid="left-sidebar-toggle"
        >
          {leftNavOpen ? (
            <KeyboardDoubleArrowLeftRoundedIcon sx={{ fontSize: navBaseIconSize }} />
          ) : (
            <KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize: navBaseIconSize }} />
          )}
          {leftNavOpen && (
            <Typography
              sx={{
                fontSize: navTinyFontSize,
              }}
            >
              Collapse
            </Typography>
          )}
        </ButtonBase>
      </Tooltip>
    </Box>
  );
});
NavFooter.displayName = 'NavFooter';

type NavState = {
  lastApp: string; // "editorial", "user", etc.
  lastFullPattern: string; // /yoursite.tndev.net/editorial/search?&qp=yes#hashmark
  navItems: NavItem[]; // navigation items
  activeKeys: KeyBoolMap; // eg: {editorialCategory: false, wireFeedsCategory: true, wireSearch: true, ...}
  openKeys: KeyBoolMap; // Same as above, but for open categories.
};

// --- Main Component ---
export const LeftNav = memo(() => {
  const location = useLocation();
  const { activeSite, sessionConfig, userInfo } = useCosmos();
  // Deferred/debounced value as some UI syncs text inputs to the url
  // so without it our effect would run on every keystroke/hashupdate etc.
  const [dbLoc] = useDebouncedValue(location, 50);
  const currentFullPattern = dbLoc.pathname + dbLoc.search + dbLoc.hash;

  // Track AI search state so links update when the toggle changes.
  // We dont need to pass resource here, but we do need to read
  // the LS value in combination with the experiments checks.
  const { isEnabledAndActive } = useEditorialAiSearchState({
    resource: null,
    sessionConfig: sessionConfig,
    userInfo: userInfo,
  });

  const { currentApp, items, header } = useMemo(() => {
    return setupNavItems({
      locationPathname: location.pathname,
      activeSite: activeSite,
      sessionConfig: sessionConfig,
      userInfo: userInfo,
      isAiSearchActive: isEnabledAndActive,
    });
  }, [activeSite, isEnabledAndActive, location.pathname, sessionConfig, userInfo]);

  const [navState, setNavState] = useState<NavState>({
    lastApp: '',
    lastFullPattern: '',
    navItems: [],
    activeKeys: {},
    openKeys: {},
  });

  // State helper function to setState for open categories.
  const handleSetOpenCategories = useCallback(
    (openKeys: React.SetStateAction<KeyBoolMap>) => {
      setNavState((prevState) => ({
        ...prevState,
        openKeys:
          typeof openKeys === 'function' ? openKeys(prevState.openKeys) : openKeys,
      }));
    },
    [setNavState],
  );

  // Recalculate active/open categories when relevant url changes.
  useEffect(() => {
    if (navState.lastFullPattern !== currentFullPattern) {
      const { activeKeys, openKeys } = getActiveOpenNavItems({
        items: items,
        activeSite: activeSite,
        locationObj: location,
      });
      // update the nav state
      setNavState({
        navItems: items,
        lastApp: currentApp,
        lastFullPattern: currentFullPattern,
        activeKeys: activeKeys,
        openKeys: openKeys,
      });
    }
  }, [
    activeSite,
    currentApp,
    currentFullPattern,
    items,
    location,
    navState.lastFullPattern,
  ]);

  return (
    <Fragment>
      <NavHeader navItem={header} />
      <NavContent
        navItems={navState.navItems}
        openCategories={navState.openKeys}
        setOpenCategories={handleSetOpenCategories}
        activeKeyMap={navState.activeKeys}
      />
      <NavFooter />
    </Fragment>
  );
});
LeftNav.displayName = 'LeftNav';
